<template>
  <div class="d-flex flex-column-fluid financing-breaches-page">
    <div class="container">
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-title">
            <h1 class="title">Breaches</h1>
            <p class="subtitle">Review financing rule breaches across businesses</p>
          </div>
          <div class="header-actions">
            <router-link
              :to="{ name: 'financing-rules' }"
              class="btn btn-dark btn-dashboard"
            >
              <i class="las la-arrow-left"></i>
              Back to Rules
            </router-link>
          </div>
        </div>
      </div>

      <div class="filters-section">
        <div class="filters-bar">
          <div class="filters-controls">
            <div class="filter-group" v-click-outside="() => { if (openDropdown === 'rule') openDropdown = null; }">
              <label class="filter-label">Rule</label>
              <div class="filter-dropdown-wrapper">
                <button
                  class="filter-dropdown-toggle"
                  :disabled="loadingRules"
                  @click.stop="toggleDropdown('rule')"
                >
                  <span :class="filters.rule_name ? 'selected-text' : 'placeholder-text'">
                    {{ filters.rule_name || 'All rules' }}
                  </span>
                  <i class="las la-angle-down dropdown-chevron"></i>
                </button>
                <div v-if="openDropdown === 'rule'" class="filter-dropdown-menu">
                  <div class="dropdown-options">
                    <div
                      class="dropdown-option"
                      :class="{ active: !filters.rule_name }"
                      @click="selectFilter('rule_name', '')"
                    >All rules</div>
                    <div
                      v-for="rule in ruleOptions"
                      :key="rule.value"
                      class="dropdown-option"
                      :class="{ active: filters.rule_name === rule.value }"
                      @click="selectFilter('rule_name', rule.value)"
                    >{{ rule.label }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="filter-group" v-click-outside="() => { if (openDropdown === 'business') openDropdown = null; }">
              <label class="filter-label">Business</label>
              <div class="filter-dropdown-wrapper">
                <button
                  class="filter-dropdown-toggle"
                  :disabled="loadingBusinesses"
                  @click.stop="toggleDropdown('business')"
                >
                  <span :class="filters.business_name ? 'selected-text' : 'placeholder-text'">
                    {{ filters.business_name || 'All businesses' }}
                  </span>
                  <i class="las la-angle-down dropdown-chevron"></i>
                </button>
                <div v-if="openDropdown === 'business'" class="filter-dropdown-menu">
                  <div class="dropdown-options">
                    <div
                      class="dropdown-option"
                      :class="{ active: !filters.business_name }"
                      @click="selectFilter('business_name', '')"
                    >All businesses</div>
                    <div
                      v-for="biz in businessOptions"
                      :key="biz.value"
                      class="dropdown-option"
                      :class="{ active: filters.business_name === biz.value }"
                      @click="selectFilter('business_name', biz.value)"
                    >{{ biz.label }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="filter-group" v-click-outside="() => { if (openDropdown === 'status') openDropdown = null; }">
              <label class="filter-label">Status</label>
              <div class="filter-dropdown-wrapper">
                <button
                  class="filter-dropdown-toggle"
                  @click.stop="toggleDropdown('status')"
                >
                  <span :class="filters.status ? 'selected-text' : 'placeholder-text'">
                    {{ statusLabel }}
                  </span>
                  <i class="las la-angle-down dropdown-chevron"></i>
                </button>
                <div v-if="openDropdown === 'status'" class="filter-dropdown-menu">
                  <div class="dropdown-options">
                    <div
                      class="dropdown-option"
                      :class="{ active: !filters.status }"
                      @click="selectFilter('status', '')"
                    >All statuses</div>
                    <div
                      class="dropdown-option"
                      :class="{ active: filters.status === 'active' }"
                      @click="selectFilter('status', 'active')"
                    >Active</div>
                    <div
                      class="dropdown-option"
                      :class="{ active: filters.status === 'resolved' }"
                      @click="selectFilter('status', 'resolved')"
                    >Resolved</div>
                    <div
                      class="dropdown-option"
                      :class="{ active: filters.status === 'silenced' }"
                      @click="selectFilter('status', 'silenced')"
                    >Silenced</div>
                    <div
                      class="dropdown-option"
                      :class="{ active: filters.status === 'awaiting_silencing_approval' }"
                      @click="selectFilter('status', 'awaiting_silencing_approval')"
                    >Awaiting approval</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            class="clear-all-filters-btn"
            :disabled="!hasActiveFilters"
            @click="clearFilters"
          >
            <i class="las la-times"></i>
            Clear filters
          </button>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner-border" role="status"></div>
        <div class="loading-text">Loading breaches...</div>
      </div>

      <div v-else-if="error" class="error-state">
        <div class="alert alert-danger">
          <i class="material-icons">error</i>
          {{ error }}
        </div>
      </div>

      <div v-else>
        <div
          class="empty-state text-center"
          v-if="isEmpty"
        >
          <i class="las la-exclamation-triangle la-5x text-muted mb-3"></i>
          <div class="empty-title">
            {{ hasActiveFilters ? 'No breaches found for your filters' : 'No breaches found' }}
          </div>
          <div class="empty-subtitle">
            {{ hasActiveFilters
              ? 'Try adjusting your filters'
              : 'There are no financing rule breaches to display' }}
          </div>
        </div>

        <div v-else class="breaches-table-container">
          <financing-table
            :columns="tableColumns"
            :data="breaches"
            :grid-template="gridTemplate"
            :total-rows="breaches.length"
            :current-page="1"
            :page-size="breaches.length"
            :show-pagination="false"
            :status-map="statusMap"
            currency="USD"
            @action="handleTableAction"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RulesService from "@/services/rules.service";
import CompaniesService from "@/services/companies.service";
import FinancingTable from "@/components/UI/FinancingTable.vue";
import { formatErrorMessage } from "@/utils/random_utils";

export default {
  name: "FinancingBreaches",
  components: {
    FinancingTable
  },
  data() {
    return {
      loading: true,
      loadingRules: false,
      loadingBusinesses: false,
      silencing: false,
      error: null,
      breaches: [],
      ruleOptions: [],
      businessOptions: [],
      openDropdown: null,
      filters: {
        rule_name: "",
        business_name: "",
        status: ""
      },
      tableColumns: [
        {
          header: "Rule",
          field: "rule_name",
          class: "rule-col",
          type: "text"
        },
        {
          header: "Business",
          field: "business_name",
          class: "business-col",
          type: "text"
        },
        {
          header: "Status",
          field: "status",
          class: "status-col",
          type: "status",
          align: "center"
        },
        {
          header: "Occurrences",
          field: "occurrence_count",
          class: "occurrences-col",
          type: "text",
          align: "center"
        },
        {
          header: "First detected",
          field: "first_detected_at",
          class: "date-col",
          type: "date",
          align: "center"
        },
        {
          header: "Resolved at",
          field: "resolved_at",
          class: "date-col",
          type: "date",
          align: "center"
        },
        {
          header: "Actions",
          field: "actions",
          class: "actions-col",
          type: "action",
          action: "silence",
          tooltip: "Silence breach",
          align: "center"
        }
      ],
      statusMap: {
        active: { label: "Active", class: "status-due" },
        resolved: { label: "Resolved", class: "status-paid" },
        silenced: { label: "Silenced", class: "status-closed" },
        awaiting_silencing_approval: { label: "Awaiting approval", class: "status-awaiting-silence" },
        new: { label: "New", class: "status-approved" },
        ongoing: { label: "Ongoing", class: "status-due" }
      }
    };
  },
  computed: {
    isEmpty() {
      return this.breaches.length === 0;
    },
    hasActiveFilters() {
      return !!(this.filters.rule_name || this.filters.business_name || this.filters.status);
    },
    statusLabel() {
      if (this.filters.status === "active") return "Active";
      if (this.filters.status === "resolved") return "Resolved";
      if (this.filters.status === "silenced") return "Silenced";
      if (this.filters.status === "awaiting_silencing_approval") return "Awaiting approval";
      return "All statuses";
    },
    gridTemplate() {
      return "20% 18% 12% 10% 14% 14% 12%";
    }
  },
  async mounted() {
    await Promise.all([
      this.loadRuleOptions(),
      this.loadBusinessOptions(),
      this.loadBreaches()
    ]);
  },
  methods: {
    async loadBreaches() {
      this.loading = true;
      this.error = null;

      try {
        const response = await RulesService.getBreaches(this.getBreachParams());
        this.breaches = this.normalizeList(response);
      } catch (error) {
        console.error("Error loading breaches:", error);
        this.error = formatErrorMessage(error) || "Failed to load breaches. Please try again.";
        this.breaches = [];
      } finally {
        this.loading = false;
      }
    },

    async loadRuleOptions() {
      this.loadingRules = true;
      try {
        const response = await RulesService.getRules({ page: 1, page_size: 200 });
        const rules = this.normalizeList(response);
        this.ruleOptions = this.toUniqueOptions(rules, ["name", "rule_name"]);
      } catch (error) {
        console.error("Error loading rules:", error);
        this.ruleOptions = [];
      } finally {
        this.loadingRules = false;
      }
    },

    async loadBusinessOptions() {
      this.loadingBusinesses = true;
      try {
        const response = await CompaniesService.query({
          page: 1,
          page_size: 200,
          list_view: "True"
        });
        const businesses = this.normalizeList(response);
        this.businessOptions = this.toUniqueOptions(businesses, ["name", "business_name"]);
      } catch (error) {
        console.error("Error loading businesses:", error);
        this.businessOptions = [];
      } finally {
        this.loadingBusinesses = false;
      }
    },

    toggleDropdown(name) {
      this.openDropdown = this.openDropdown === name ? null : name;
    },

    selectFilter(field, value) {
      this.filters[field] = value;
      this.openDropdown = null;
      this.loadBreaches();
    },

    handleTableAction(actionData) {
      if (actionData.action === "silence") {
        this.confirmSilenceBreach(actionData.row);
      } else if (actionData.action === "unsilence") {
        this.confirmUnsilenceBreach(actionData.row);
      }
    },

    confirmSilenceBreach(breach) {
      if (!breach || breach.status !== "active") return;

      const ruleName = breach.rule_name || "this rule";
      const businessName = breach.business_name || "this business";
      const message = this.$createElement("div", { class: "text-center" }, [
        this.$createElement("i", {
          class: "las la-bell-slash text-warning",
          style: "font-size: 3rem; margin-bottom: 1rem;"
        }),
        this.$createElement(
          "p",
          { class: "mb-2" },
          `Silence the "${ruleName}" breach for ${businessName}?`
        ),
        this.$createElement(
          "p",
          { class: "text-muted mb-0", style: "font-size: 0.875rem;" },
          "Silenced breaches will stop appearing as active and will no longer trigger alerts."
        )
      ]);

      this.$bvModal
        .msgBoxConfirm(message, {
          title: "Silence Breach",
          buttonSize: "md",
          okTitle: "Silence",
          okVariant: "warning",
          cancelTitle: "Cancel",
          cancelVariant: "secondary",
          centered: true,
          modalClass: "silence-confirmation-modal",
          headerClass: "border-0 pb-0",
          bodyClass: "pt-2",
          footerClass: "border-0 pt-0"
        })
        .then((confirmed) => {
          if (confirmed) {
            this.updateBreachStatus(breach, "silenced", "silenced", "Failed to silence breach. Please try again.");
          }
        });
    },

    confirmUnsilenceBreach(breach) {
      if (!breach || breach.status !== "silenced") return;

      const ruleName = breach.rule_name || "this rule";
      const businessName = breach.business_name || "this business";
      const message = this.$createElement("div", { class: "text-center" }, [
        this.$createElement("i", {
          class: "las la-bell text-primary",
          style: "font-size: 3rem; margin-bottom: 1rem;"
        }),
        this.$createElement(
          "p",
          { class: "mb-2" },
          `Unsilence the "${ruleName}" breach for ${businessName}?`
        ),
        this.$createElement(
          "p",
          { class: "text-muted mb-0", style: "font-size: 0.875rem;" },
          "This breach will become active again and can trigger alerts."
        )
      ]);

      this.$bvModal
        .msgBoxConfirm(message, {
          title: "Unsilence Breach",
          buttonSize: "md",
          okTitle: "Unsilence",
          okVariant: "primary",
          cancelTitle: "Cancel",
          cancelVariant: "secondary",
          centered: true,
          modalClass: "silence-confirmation-modal",
          headerClass: "border-0 pb-0",
          bodyClass: "pt-2",
          footerClass: "border-0 pt-0"
        })
        .then((confirmed) => {
          if (confirmed) {
            this.updateBreachStatus(breach, "active", "unsilenced", "Failed to unsilence breach. Please try again.");
          }
        });
    },

    async updateBreachStatus(breach, status, successLabel, errorFallback) {
      if (this.silencing || !breach || !breach.id) return;

      try {
        this.silencing = true;
        await RulesService.updateBreach(breach.id, { status });
        await this.loadBreaches();

        this.$bvToast.toast(`Breach "${breach.rule_name || "selected"}" has been ${successLabel}.`, {
          title: "Success",
          variant: "success",
          solid: true,
          autoHideDelay: 3000,
          toaster: "b-toaster-top-center"
        });
      } catch (error) {
        console.error("Error updating breach status:", error);
        const errorMessage = formatErrorMessage(error) || errorFallback;
        this.$bvToast.toast(errorMessage, {
          title: "Error",
          variant: "danger",
          solid: true,
          autoHideDelay: 4000,
          toaster: "b-toaster-top-center"
        });
      } finally {
        this.silencing = false;
      }
    },

    clearFilters() {
      this.filters = {
        rule_name: "",
        business_name: "",
        status: ""
      };
      this.openDropdown = null;
      this.loadBreaches();
    },

    getBreachParams() {
      const params = {};
      if (this.filters.rule_name) params.rule_name = this.filters.rule_name;
      if (this.filters.business_name) params.business_name = this.filters.business_name;
      if (this.filters.status) params.status = this.filters.status;
      return params;
    },

    toUniqueOptions(items, fields) {
      const seen = {};
      return items.reduce((options, item) => {
        const value = this.getOptionValue(item, fields);
        if (!value || seen[value]) return options;
        seen[value] = true;
        options.push({ value, label: value });
        return options;
      }, []);
    },

    getOptionValue(item, fields) {
      if (item === undefined || item === null) return "";
      if (typeof item === "string" || typeof item === "number") {
        return String(item).trim();
      }
      for (let i = 0; i < fields.length; i += 1) {
        const value = item[fields[i]];
        if (value !== undefined && value !== null && value !== "") {
          return String(value).trim();
        }
      }
      return "";
    },

    normalizeList(response) {
      if (Array.isArray(response)) return response;
      if (!response) return [];
      if (Array.isArray(response.data)) return response.data;
      if (response.data && Array.isArray(response.data.data)) return response.data.data;
      if (Array.isArray(response.results)) return response.results;
      return [];
    }
  }
};
</script>

<style scoped lang="scss">
@import './index.scss';

.financing-breaches-page {
  .header-actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 0 0 auto;
    max-width: none;

    .btn-dashboard {
      background: linear-gradient(135deg, #212529 0%, #495057 100%);
      border: none;
      border-radius: 0.375rem;
      font-weight: 500;
      padding: 0.5rem 1rem;
      color: #ffffff;
      white-space: nowrap;

      i {
        margin-right: 0.5rem;
      }
    }
  }

  .filters-section {
    background: #fff;
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
    margin-bottom: 1rem;
  }

  .filters-bar {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
  }

  .filters-controls {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    flex: 1;
    min-width: 0;
  }

  .filter-group {
    position: relative;
    flex-shrink: 0;
  }

  .filter-label {
    display: block;
    font-weight: 600;
    font-size: 0.8125rem;
    color: #1e293b;
    margin-bottom: 0.375rem;
  }

  .filter-dropdown-wrapper {
    position: relative;
  }

  .filter-dropdown-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #fff;
    font-size: 13px;
    color: #374151;
    cursor: pointer;
    white-space: nowrap;
    transition: border-color 0.2s, box-shadow 0.2s;
    min-width: 140px;

    &:hover {
      border-color: #9ca3af;
    }

    &:focus {
      outline: none;
      border-color: #6366f1;
      box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .placeholder-text {
      color: #9ca3af;
    }

    .selected-text {
      color: #111827;
      font-weight: 500;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 160px;
    }

    .dropdown-chevron {
      margin-left: auto;
      font-size: 12px;
      color: #9ca3af;
      transition: transform 0.2s;
    }
  }

  .filter-dropdown-menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    min-width: 100%;
    max-height: 260px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    z-index: 100;
    overflow: hidden;

    .dropdown-options {
      max-height: 240px;
      overflow-y: auto;
      padding: 4px 0;
    }

    .dropdown-option {
      padding: 8px 14px;
      font-size: 13px;
      color: #374151;
      cursor: pointer;
      transition: background 0.15s;
      white-space: nowrap;

      &:hover {
        background: #f3f4f6;
      }

      &.active {
        background: #eef2ff;
        color: #4338ca;
        font-weight: 600;
      }
    }
  }

  .clear-all-filters-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #fff;
    font-size: 13px;
    color: #6b7280;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s;
    flex-shrink: 0;

    &:hover:not(:disabled) {
      border-color: #ef4444;
      color: #ef4444;
      background: #fef2f2;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    i {
      font-size: 14px;
    }
  }

  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;

    .spinner-border {
      width: 3rem;
      height: 3rem;
      color: #007bff;
      margin-bottom: 1rem;
    }

    .loading-text {
      color: #6c757d;
      font-weight: 500;
    }
  }

  .error-state {
    .alert {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      i {
        font-size: 1.25rem;
      }
    }
  }

  .empty-state {
    padding: 3rem;
    background: white;
    border-radius: 0.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .empty-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #495057;
      margin-bottom: 0.5rem;
    }

    .empty-subtitle {
      color: #6c757d;
    }
  }

  .breaches-table-container {
    background: white;
    border-radius: 0.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    :deep(.financing-table) {
      .grid-header .header-row {
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      }

      .grid-body .data-row {
        .rule-col {
          font-weight: 600;
          color: #212529;
        }

        .business-col {
          color: #495057;
        }

        .occurrences-col {
          font-weight: 600;
        }

        .actions-col {
          .cell-content {
            display: flex;
            justify-content: center;
            align-items: center;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
.silence-confirmation-modal {
  .modal-header {
    .modal-title {
      font-weight: 600;
      color: #d97706;
    }
  }

  .modal-body {
    .las.la-bell-slash,
    .las.la-bell {
      display: block;
      margin: 0 auto 1rem;
    }
  }

  .modal-footer {
    .btn {
      border-radius: 0.375rem;
      font-weight: 500;
      padding: 0.5rem 1.5rem;
    }
  }
}
</style>
